import type { VanillaLinkProps } from '../Link/index.ts';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'motion/react';

export interface IconButtonProps {
  animate?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  focusRing?: boolean;
  icon?: React.ReactNode;
  iconClassName?: string;
  inline?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  onClick?: React.MouseEventHandler | (() => void);
  renderLink?: (props: VanillaLinkProps) => React.ReactNode;
  subdued?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

export function IconButton({
  animate,
  ariaLabel,
  disabled,
  focusRing,
  icon,
  iconClassName,
  inline,
  label,
  labelClassName,
  onClick,
  renderLink,
  subdued,
  size = 'md'
}: IconButtonProps) {
  const shouldReduceMotion = useReducedMotion();
  const IconContainerComponent = animate ? motion.div : 'div';
  const iconContainerProps =
    animate && !disabled && !shouldReduceMotion ? { whileTap: { scale: 0.8 } } : undefined;

  const content = (
    <>
      <IconContainerComponent
        className={clsx(
          'flex h-full items-center justify-center',
          size === 'xs' && '*:size-4',
          size === 'sm' && '*:size-5',
          size === 'md' && '*:size-7',
          size === 'lg' && '*:size-9',
          iconClassName ||
            clsx(
              disabled && 'text-gray-300',
              subdued
                ? 'text-gray-400 group-hover:text-gray-600'
                : 'text-gray-700 group-hover:text-gray-800'
            )
        )}
        {...iconContainerProps}
      >
        {icon}
      </IconContainerComponent>

      {!!label && (
        <span
          className={clsx(
            size === 'xs' && 'text-xs font-medium',
            size === 'sm' && 'text-xs font-medium',
            size === 'md' && 'text-sm font-normal',
            size === 'lg' && 'text-base font-normal',
            labelClassName ||
              clsx(
                disabled && 'text-gray-300',
                subdued
                  ? 'text-gray-500 group-hover:text-gray-700'
                  : 'text-gray-700 group-hover:text-gray-800'
              )
          )}
        >
          {label}
        </span>
      )}
    </>
  );

  const rootComponentProps: React.ComponentProps<'button'> & VanillaLinkProps = {
    disabled,
    className: clsx(
      'h-full items-center gap-x-2 rounded-sm whitespace-nowrap',
      !disabled && 'group',
      inline ? 'flex-inline' : 'flex',
      focusRing &&
        'focus:bg-white focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-hidden'
    ),
    onClick
  };

  if (renderLink) {
    return renderLink({
      ...rootComponentProps,
      children: content,
      plain: true
    });
  }

  return (
    <button aria-label={ariaLabel} type="button" {...rootComponentProps}>
      {content}
    </button>
  );
}
